<script lang="ts">
export default {
  name: 'PaymentMethodPreviewModal',
}
</script>
<script setup lang="ts" >
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { KkModal, KkIcon, KkButton, KkLoading } from '../../../../components/base'
import { KkTabList } from '../../../../components/ui'
import { KkPaymentMethodPreview } from '../../../paymentMethod'
import type {ServiceType, PaymentMethodPreview} from 'src/biz_spa/domains/paymentMethod/types'
import { ServiceTypes} from 'src/biz_spa/domains/paymentMethod/types'
const { t } = useI18n()

interface Props {
  isVisible: boolean
  showSubmit: boolean
  loading: boolean
  title: string
  paymentMethodSynced: boolean
  paymentMethodPreview: PaymentMethodPreview
}

const props = defineProps<Props>()
const emit = defineEmits(['submit', 'close'])

const selectableServiceTypes = computed<ServiceType[]>(() => {
  return ServiceTypes.filter(serviceType => props.paymentMethodPreview[serviceType])
})

const selectableServiceTypeTabs = computed(() => {
  const result: { [type: string]: string } = {}
  selectableServiceTypes.value.forEach(key => result[key] = t(`labels.sites.${key}`))
  return result
})

const errorServiceTypes = computed<string[]>(() => {
  const result: string[] = []
  Object.entries(props.paymentMethodPreview).forEach(([key, value]) => {
    if (value?.errors?.length) result.push(key)
  })
  return result
})

const displayedStatusIconServiceTypes = computed<string[]>(() => {
  return props.paymentMethodSynced ? ['google', ...errorServiceTypes.value] : errorServiceTypes.value
})

const hasError = computed<boolean>(() => {
  return !!errorServiceTypes.value.length
})

const selectedServiceType = ref<ServiceType>()
/** Watch */
watch(selectableServiceTypes, () => {
  if (!selectableServiceTypes.value.length) return
  selectedServiceType.value = selectableServiceTypes.value[0]
})

const changeSelectedServiceType = (value: ServiceType): void => {
  selectedServiceType.value = value
}
</script>

<template>
  <KkModal :is-visible="props.isVisible" width="1076px" @change="emit('close')">
    <div class="p-modal__content__header">
      {{ props.title }}
      <span v-if="hasError" class="error">
        {{ t('pages.CompaniesLocationStorePaymentMethodsEdit.preview.hasError') }}
      </span>
    </div>
    <KkTabList
      class="tabs"
      :menu-list="selectableServiceTypeTabs"
      :selected-tab-key="selectedServiceType || ''"
      :icon-keys="displayedStatusIconServiceTypes"
      @click-menu="changeSelectedServiceType($event as ServiceType)"
    >
      <template #icon="slotProps">
        <template v-if="slotProps.key === 'google'">
          <KkIcon name="protection" color="#79acb0" class="tabs__icon" />
        </template>
        <template v-else>
          <KkIcon name="exclamation" color="#E9446A" class="tabs__icon" />
        </template>
      </template>
    </KkTabList>
    <div class="p-modal__content__body">
      <KkLoading v-if="loading" />
      <div v-else>
        <template v-if="selectedServiceType">
          <KkPaymentMethodPreview
            :service-type="selectedServiceType"
            :payment-method-preview="props.paymentMethodPreview"
          />
        </template>
        <template v-else>
          {{ t('pages.CompaniesLocationStorePaymentMethodsEdit.preview.noConnectedService') }}
        </template>
      </div>
    </div>
    <div class="p-modal__content__footer">
      <div class="c-button c-button--right">
        <KkButton
          class="footerCancelBtn"
          color="secondary"
          outlined
          :text="t('labels.close')"
          @click="emit('close')"
        />
        <KkButton
          v-if="props.showSubmit"
          :disabled="hasError || loading"
          color="primary"
          outlined
          :text="t('labels.confirm')"
          @click="emit('submit')"
        />
      </div>
    </div>
  </KkModal>
</template>

<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';
.paymentMethod {
  height: 100%;
  &__paymentTypeSection {
    width: 25%;
  }
  &__section {
    display: flex;
    align-items: center;
    background-color: #f8f9fa;
    height: 100%;
  }
  &__icon {
    width: 12px;
    margin-left: 4px;
  }
  td:empty {
    display: table-cell;
  }
}

.tabs {
  padding: 16px 32px;

  &__icon {
    width: 14px;
    margin-left: 4px;
  }
}

.footerCancelBtn {
  margin-right: 8px;
}

.error {
  margin-left: 24px;
  font-size: 14px;
  color: $color-accent-primary;
  font-weight: normal;
}
</style>
